import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Col, Form, Row } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { schemaFormEvent } from "../../../features/forms/schemas/schemaFormEvent";
import { Event } from "../../../types/events";
import { Site } from "../../../types/sites";
import { isDev } from "../../../utils/dev";
import Br from "../../ui/Br";
import spacing from "../../ui/spacingConfig";
import FieldDate from "../fields/FieldDate";
import FieldInput from "../fields/FieldInput";
import FieldSelect from "../fields/FieldSelect";
import FieldTextArea from "../fields/FieldTextArea";
import FieldUpload from "../fields/FieldUpload/FieldUpload";
import { getLabel } from "../utils/ShowIsRequired";
import "./FormEventNew.module.scss";
import styles from "./FormEventNew.module.scss";
import useSubmit from "./useSubmit";

export type FormEventSchema = z.infer<typeof schemaFormEvent>;

type Props = {
  isEditMode?: boolean;
  event?: Event;
  sites?: Site[];
};

const getDefaultValue = (edit: boolean, event?: Event): FormEventSchema => {
  if (!edit || !event) {
    return {
      eventType: "EVENT",
      name: "",
      description: "",
      firstMeasure: "",
      site: "",
      localisation: "",
      dateEvt: dayjs(new Date()),
    };
  }
  return {
    eventType: "EVENT",
    name: event.name || "",
    description: event.description || "",
    firstMeasure: event.firstMeasure,
    site: event.site ? String(event.site.id) : "",
    localisation: event.localisation,
    dateEvt: dayjs(event.dateEvt),
  };
};

const FormEventNew: React.FC<Props> = ({ sites, isEditMode, event }) => {
  const { onSubmit, isLoading } = useSubmit(isEditMode, event);

  const ColSpacingSecondary = {
    labelCol: {
      span: spacing.xs,
    },
    wrapperCol: {
      span: spacing.xs,
    },
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormEventSchema>({
    defaultValues: getDefaultValue(!!isEditMode, event),
    resolver: zodResolver(schemaFormEvent),
  });

  if (isDev) {
    const formValues = watch(); // Get the form values state
    console.log("formValues", formValues);
    console.log("errors", errors);
  }

  return (
    <Form
      labelCol={{ span: spacing.tiny }}
      wrapperCol={{ span: spacing.sPlus }}
      layout={"horizontal"}
      onFinish={handleSubmit(onSubmit)}
      className={styles.root}
      labelAlign="left"
      colon={false}
    >
      <Row gutter={spacing.m} className={styles.firstRow}>
        <Col span={14}>
          <Card title="Description" bordered={false}>
            <FieldInput<FormEventSchema>
              errorMessage={errors?.name?.message}
              control={control}
              name="name"
              label={getLabel("Titre", "name")}
              placeholder="Titre..."
            />
            <FieldTextArea<FormEventSchema>
              errorMessage={errors?.description?.message}
              control={control}
              name="description"
              label={getLabel("Description", "description")}
              placeholder="Description..."
              className={styles.textArea}
            />
            <FieldTextArea<FormEventSchema>
              errorMessage={errors?.firstMeasure?.message}
              control={control}
              name="firstMeasure"
              label={getLabel("Mesure immÃ©diate", "firstMeasure")}
              placeholder="Mesure immÃ©diate..."
            />
          </Card>
        </Col>
        <Col span={10}>
          <Card title="Date et lieu" bordered={false}>
            <FieldSelect<FormEventSchema>
              errorMessage={errors?.site?.message}
              control={control}
              name="site"
              label={getLabel("Site", "site")}
              labelCol={ColSpacingSecondary.labelCol}
              wrapperCol={ColSpacingSecondary.wrapperCol}
              placeholder="Choisir un site"
              options={
                sites?.map((site) => ({
                  value: String(site.id),
                  label: site.name,
                })) || []
              }
            />
            <FieldInput<FormEventSchema>
              errorMessage={errors?.localisation?.message}
              control={control}
              name="localisation"
              label={getLabel("Localisation", "localisation")}
              placeholder="PrÃ©cision..."
              labelCol={ColSpacingSecondary.labelCol}
              wrapperCol={ColSpacingSecondary.wrapperCol}
            />
            <FieldDate<FormEventSchema>
              errorMessage={errors?.dateEvt?.message}
              control={control}
              name="dateEvt"
              label={getLabel("Date", "dateEvt")}
              labelCol={ColSpacingSecondary.labelCol}
              wrapperCol={ColSpacingSecondary.wrapperCol}
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row>
        <Col span={spacing.m}>
          <Card title="PiÃ¨ces jointes" bordered={false}>
            <FieldUpload<FormEventSchema>
              errorMessage={errors.attachedFiles?.message}
              name="attachedFiles"
              control={control}
              listType="picture"
              label=""
              placeholder=""
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row justify="end">
        <Button
          type="primary"
          htmlType="submit"
          className="large-padding-btn"
          loading={isLoading}
        >
          {isEditMode ? "Modifier" : "Publier"}
        </Button>
      </Row>
    </Form>
  );
};

export default FormEventNew;
